import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import { Input } from '../components/Input';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  credits: number;
  isAdmin: boolean;
  role: 'user' | 'mod' | 'admin';
  createdAt: string;
}

interface UserWorkspace {
  id: string;
  name: string;
  createdAt: string;
  postsCount: number;
  assetsCount: number;
  logoUrl: string | null;
}

const PageWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.background};
  border-bottom: ${({ theme }) => theme.borders.thin};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const Tr = styled.tr`
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const RoleSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: ${({ theme }) => theme.borders.thin};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CreditsCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const CreditsInput = styled(Input)`
  width: 100px;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
`;

const SaveButton = styled(Button)`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const LoadingText = styled.p`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ErrorText = styled.p`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.error};
`;

const TotalUsers = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: ${({ theme }) => theme.fontWeight.normal};
`;

const WorkspacesButton = styled(Button)`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const WorkspacesRow = styled.tr`
  background-color: ${({ theme }) => theme.colors.background};
`;

const WorkspacesCell = styled.td`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
`;

const WorkspacesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const WorkspaceCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const WorkspaceLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  object-fit: cover;
`;

const WorkspaceLogoPlaceholder = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const WorkspaceName = styled.span`
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  flex: 1;
`;

const WorkspaceStat = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const NoWorkspaces = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
`;

export function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCredits, setEditingCredits] = useState<Record<string, number>>({});
  const [savingCredits, setSavingCredits] = useState<Record<string, boolean>>({});
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [userWorkspaces, setUserWorkspaces] = useState<Record<string, UserWorkspace[]>>({});
  const [loadingWorkspaces, setLoadingWorkspaces] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
      return;
    }
    loadUsers();
  }, [user, navigate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<{ users: AdminUser[] }>('/api/admin/users');
      setUsers(response.users);
      const creditsMap: Record<string, number> = {};
      response.users.forEach((u) => {
        creditsMap[u.id] = u.credits;
      });
      setEditingCredits(creditsMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreditsChange = (userId: string, value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      setEditingCredits((prev) => ({ ...prev, [userId]: numValue }));
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await apiClient.put(`/api/admin/users/${userId}/role`, { role: newRole });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, role: newRole as 'user' | 'mod' | 'admin', isAdmin: newRole === 'admin' } : u))
      );
    } catch (err) {
      console.error('Failed to update role:', err);
      alert('Failed to update role');
    }
  };

  const handleSaveCredits = async (userId: string) => {
    const newCredits = editingCredits[userId];
    const currentUser = users.find((u) => u.id === userId);
    if (currentUser && currentUser.credits === newCredits) return;

    setSavingCredits((prev) => ({ ...prev, [userId]: true }));
    try {
      await apiClient.put(`/api/admin/users/${userId}/credits`, { credits: newCredits });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, credits: newCredits } : u))
      );
    } catch (err) {
      console.error('Failed to update credits:', err);
      alert('Failed to update credits');
      if (currentUser) {
        setEditingCredits((prev) => ({ ...prev, [userId]: currentUser.credits }));
      }
    } finally {
      setSavingCredits((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const toggleWorkspaces = async (userId: string) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      return;
    }

    setExpandedUser(userId);

    if (userWorkspaces[userId]) return;

    setLoadingWorkspaces((prev) => ({ ...prev, [userId]: true }));
    try {
      const response = await apiClient.get<{ workspaces: UserWorkspace[] }>(`/api/admin/users/${userId}/workspaces`);
      setUserWorkspaces((prev) => ({ ...prev, [userId]: response.workspaces }));
    } catch (err) {
      console.error('Failed to load workspaces:', err);
    } finally {
      setLoadingWorkspaces((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const [resetting, setResetting] = useState(false);
  const [defaultCredits, setDefaultCredits] = useState(1000);
  const [savingCreditsDefault, setSavingCreditsDefault] = useState(false);

  // Load admin settings
  useEffect(() => {
    apiClient.get<{ defaultCredits: number }>('/api/admin/settings')
      .then(s => setDefaultCredits(s.defaultCredits))
      .catch(() => {});
  }, []);

  const saveDefaultCredits = async () => {
    setSavingCreditsDefault(true);
    try {
      await apiClient.put('/api/admin/settings', { defaultCredits });
      alert(`Default credits set to ${defaultCredits} for new accounts.`);
    } catch {
      alert('Failed to save.');
    } finally {
      setSavingCreditsDefault(false);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('DELETE ALL WORKSPACES, POSTS, AND STORAGE?\n\nThis cannot be undone. Global templates will be preserved.\n\nType OK to confirm.')) return;
    if (!window.confirm('FINAL WARNING: This will permanently delete everything. Are you absolutely sure?')) return;
    setResetting(true);
    try {
      await apiClient.delete('/api/admin/reset-all');
      alert('Platform reset complete. All workspaces, posts, and storage cleared.');
      window.location.reload();
    } catch (err) {
      console.error('Reset failed:', err);
      alert('Reset failed. Check console.');
    } finally {
      setResetting(false);
    }
  };

  if (!user?.isAdmin) {
    return null;
  }

  return (
    <PageWrapper>
      <PageHeader>
        <Title>
          Admin Panel <TotalUsers>({users.length} users)</TotalUsers>
        </Title>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button $variant="outline" onClick={handleResetAll} disabled={resetting}
            style={{ color: '#EF4444', borderColor: '#EF4444' }}>
            {resetting ? 'Resetting...' : 'Reset All Data'}
          </Button>
          <Button $variant="outline" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </PageHeader>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>New account starting credits:</span>
        <Input
          type="number"
          value={defaultCredits}
          onChange={e => setDefaultCredits(Math.max(0, parseInt(e.target.value, 10) || 0))}
          style={{ width: 100 }}
        />
        <Button $size="sm" onClick={saveDefaultCredits} disabled={savingCreditsDefault}>
          {savingCreditsDefault ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {loading && <LoadingText>Loading users...</LoadingText>}
      {error && <ErrorText>{error}</ErrorText>}

      {!loading && !error && (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Role</Th>
              <Th>Credits</Th>
              <Th>Registered</Th>
              <Th>Workspaces</Th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <>
                <Tr key={u.id}>
                  <Td>{u.name}</Td>
                  <Td>{u.email}</Td>
                  <Td>
                    <RoleSelect
                      value={u.role || 'user'}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      disabled={u.id === user?.id}
                    >
                      <option value="user">User</option>
                      <option value="mod">Mod</option>
                      <option value="admin">Admin</option>
                    </RoleSelect>
                  </Td>
                  <Td>
                    <CreditsCell>
                      <CreditsInput
                        type="number"
                        min="0"
                        value={editingCredits[u.id] ?? u.credits}
                        onChange={(e) => handleCreditsChange(u.id, e.target.value)}
                      />
                      {editingCredits[u.id] !== u.credits && (
                        <SaveButton
                          onClick={() => handleSaveCredits(u.id)}
                          disabled={savingCredits[u.id]}
                        >
                          {savingCredits[u.id] ? '...' : 'Save'}
                        </SaveButton>
                      )}
                    </CreditsCell>
                  </Td>
                  <Td>{new Date(u.createdAt).toLocaleDateString()}</Td>
                  <Td>
                    <WorkspacesButton
                      $variant="outline"
                      onClick={() => toggleWorkspaces(u.id)}
                    >
                      {expandedUser === u.id ? 'Hide' : 'View'}
                    </WorkspacesButton>
                  </Td>
                </Tr>
                {expandedUser === u.id && (
                  <WorkspacesRow key={`${u.id}-workspaces`}>
                    <WorkspacesCell colSpan={6}>
                      {loadingWorkspaces[u.id] ? (
                        <NoWorkspaces>Loading workspaces...</NoWorkspaces>
                      ) : !userWorkspaces[u.id] || userWorkspaces[u.id].length === 0 ? (
                        <NoWorkspaces>No workspaces</NoWorkspaces>
                      ) : (
                        <WorkspacesList>
                          {userWorkspaces[u.id].map((ws) => (
                            <WorkspaceCard key={ws.id} onClick={() => navigate(`/workspace/${ws.id}`)}>
                              {ws.logoUrl ? (
                                <WorkspaceLogo src={ws.logoUrl} alt={ws.name} />
                              ) : (
                                <WorkspaceLogoPlaceholder>
                                  {ws.name.charAt(0).toUpperCase()}
                                </WorkspaceLogoPlaceholder>
                              )}
                              <WorkspaceName>{ws.name}</WorkspaceName>
                              <WorkspaceStat>{ws.postsCount} posts</WorkspaceStat>
                              <WorkspaceStat>{ws.assetsCount} assets</WorkspaceStat>
                              <WorkspaceStat>{new Date(ws.createdAt).toLocaleDateString()}</WorkspaceStat>
                            </WorkspaceCard>
                          ))}
                        </WorkspacesList>
                      )}
                    </WorkspacesCell>
                  </WorkspacesRow>
                )}
              </>
            ))}
          </tbody>
        </Table>
      )}
    </PageWrapper>
  );
}
